import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bell, BellRing, X, TrendingUp, ArrowDownToLine, Megaphone, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { playWinChime } from '../utils/winChime';
import CryptoIcon from './CryptoIcon';
import { PRICE_ALERTS_CHANGED } from './PriceAlertButton';

import { fmtFixed } from '../utils/format';
const SWIPE_CLOSE_THRESHOLD = 90;
const POLL_MS = 45000;

const iconForType = (type) => {
  if (type === 'trade_win') return TrendingUp;
  if (type === 'deposit') return ArrowDownToLine;
  if (type === 'price_alert') return BellRing;
  return Megaphone;
};

const accentForType = (type) => {
  if (type === 'trade_win') return 'text-emerald-400 bg-emerald-500/10';
  if (type === 'deposit') return 'text-cyan-400 bg-cyan-500/10';
  if (type === 'price_alert') return 'text-amber-400 bg-amber-500/10';
  return 'text-purple-400 bg-purple-500/10';
};

const showPriceAlertToast = (item) => {
  toast.custom(
    (t) => (
      <div
        className="flex items-start gap-3 rounded-2xl px-4 py-3 shadow-2xl border bg-[#0d1424] border-amber-400/50"
        style={{ minWidth: 300, maxWidth: 380 }}
        onClick={() => toast.dismiss(t)}
        role="alert"
        data-testid="price-alert-toast"
      >
        {item.symbol ? <CryptoIcon symbol={item.symbol} size={36} /> : <BellRing className="h-5 w-5 text-amber-400" />}
        <div className="min-w-0">
          <p className="text-amber-300 font-bold text-sm leading-tight">{item.title}</p>
          <p className="text-gray-300 text-xs mt-0.5 leading-snug">{item.message}</p>
        </div>
      </div>
    ),
    { duration: 8000, position: 'top-center' },
  );
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

// Golden toast for a trade win — matches Sonner's API while looking custom.
const showGoldenWinToast = (win) => {
  toast.custom(
    (t) => (
      <div
        className="flex items-start gap-3 rounded-2xl px-4 py-3 shadow-2xl border"
        style={{
          background: 'linear-gradient(135deg, #78550a 0%, #c9971b 55%, #f9d976 100%)',
          borderColor: 'rgba(255, 215, 100, 0.55)',
          minWidth: 300,
          maxWidth: 380,
        }}
        onClick={() => toast.dismiss(t)}
        role="alert"
        data-testid="win-toast"
      >
        <div className="shrink-0 mt-0.5 p-2 rounded-full" style={{ background: 'rgba(0,0,0,0.25)' }}>
          <Trophy className="h-5 w-5" style={{ color: '#fff8c9' }} />
        </div>
        <div className="min-w-0">
          <p className="text-white font-bold text-sm leading-tight">
            You won +${fmtFixed(Number(win.amount || 0), 2)}!
          </p>
          <p className="text-white/85 text-xs mt-0.5 leading-snug">
            Your {win.symbol || 'trade'} settled in your favor.
          </p>
        </div>
      </div>
    ),
    { duration: 6000, position: 'top-center' },
  );
};

const NotificationBell = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  const touchStartYRef = useRef(null);
  const dragOffsetRef = useRef(0);
  const seenWinIdsRef = useRef(null); // Set of ids we've already alerted on
  const firstLoadRef = useRef(true);

  const fetchFeed = useCallback(async ({ isPoll = false } = {}) => {
    if (!user?.id) return;
    try {
      if (!isPoll) setLoading(true);
      try {
        await api.post('/price-alerts/check');
      } catch { /* non-fatal */ }
      const { data } = await api.get('/notifications/feed?limit=20');
      const newItems = data.items || [];
      setItems(newItems);
      setUnread(data.unread_count || 0);

      // Detect newly-arrived trade wins / price alerts to fire toasts.
      // On the very first load we treat everything as already-seen so we
      // don't spam the user on refresh.
      const wins = newItems.filter((i) => i.type === 'trade_win' || i.type === 'price_alert');
      if (firstLoadRef.current) {
        seenWinIdsRef.current = new Set(wins.map((w) => w.id));
        firstLoadRef.current = false;
      } else if (seenWinIdsRef.current) {
        const fresh = wins.filter((w) => !seenWinIdsRef.current.has(w.id));
        if (fresh.some((w) => w.type === 'price_alert')) {
          window.dispatchEvent(new Event(PRICE_ALERTS_CHANGED));
        }
        // Newest-first — the API sorts desc. Alert them oldest-to-newest so
        // the newest chime is the last one heard.
        fresh.slice().reverse().forEach((w) => {
          seenWinIdsRef.current.add(w.id);
          if (w.type === 'price_alert') {
            showPriceAlertToast(w);
          } else {
            showGoldenWinToast(w);
          }
          playWinChime();
        });
      }
    } catch (e) {
      // silent — bell should never break the header
    } finally {
      if (!isPoll) setLoading(false);
    }
  }, [user?.id]);

  // Poll every 45s so the badge stays fresh
  useEffect(() => {
    if (!user?.id) return undefined;
    fetchFeed({ isPoll: false });
    const iv = setInterval(() => fetchFeed({ isPoll: true }), POLL_MS);
    const onAlert = () => fetchFeed({ isPoll: true });
    window.addEventListener('phantomtrades:price-alert', onAlert);
    return () => {
      clearInterval(iv);
      window.removeEventListener('phantomtrades:price-alert', onAlert);
    };
  }, [user?.id, fetchFeed]);

  const closeSheet = () => {
    setOpen(false);
    setDragOffset(0);
    dragOffsetRef.current = 0;
  };

  const openSheet = async () => {
    setOpen(true);
    // Optimistically zero the badge — sheet is open, user is looking at feed.
    setUnread(0);
    // Persist the "seen" cursor server-side BEFORE re-fetching so the fresh
    // feed response returns unread_count=0 and no flash occurs.
    try {
      await api.post('/notifications/mark-read');
    } catch {
      /* non-fatal — a subsequent poll will re-fetch the true count */
    }
    await fetchFeed({ isPoll: false });
  };

  // Escape + history back + body scroll lock while open
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeSheet(); };
    window.history.pushState({ notificationsOpen: true }, '');
    const onPop = () => closeSheet();
    window.addEventListener('keydown', onKey);
    window.addEventListener('popstate', onPop);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onPop);
      document.body.style.overflow = prev;
      if (window.history.state && window.history.state.notificationsOpen) {
        window.history.back();
      }
    };
  }, [open]);

  // Swipe-down to dismiss
  const onTouchStart = (e) => {
    touchStartYRef.current = e.touches[0].clientY;
    dragOffsetRef.current = 0;
    setDragOffset(0);
  };
  const onTouchMove = (e) => {
    if (touchStartYRef.current == null) return;
    const dy = e.touches[0].clientY - touchStartYRef.current;
    if (dy > 0) { dragOffsetRef.current = dy; setDragOffset(dy); }
  };
  const onTouchEnd = () => {
    if (dragOffsetRef.current > SWIPE_CLOSE_THRESHOLD) closeSheet();
    else { dragOffsetRef.current = 0; setDragOffset(0); }
    touchStartYRef.current = null;
  };

  if (!user?.id) return null;

  const badgeText = unread > 9 ? '9+' : String(unread);

  return (
    <>
      <button
        type="button"
        onClick={openSheet}
        className="relative p-2 rounded-full text-gray-300 hover:text-white hover:bg-white/5 transition-colors focus:outline-none focus-visible:outline-none"
        aria-label="Open notifications"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="notification-bell-btn"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white text-[10px] font-bold shadow-[0_0_0_2px_var(--bg-secondary)]"
            data-testid="notification-bell-badge"
          >
            {badgeText}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm"
          onClick={closeSheet}
          role="presentation"
          data-testid="notification-sheet-overlay"
        >
          <div
            className="absolute bottom-0 left-0 right-0 md:bottom-auto md:top-16 md:right-4 md:left-auto md:w-[380px] md:rounded-2xl bg-[#0a1628] border border-purple-500/20 rounded-t-3xl shadow-2xl overflow-hidden"
            style={{
              paddingBottom: 'env(safe-area-inset-bottom)',
              transform: dragOffset ? `translateY(${dragOffset}px)` : undefined,
              transition: dragOffset ? 'none' : 'transform 200ms ease-out',
              maxHeight: '75vh',
            }}
            onClick={e => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            role="dialog"
            aria-modal="true"
            aria-labelledby="notification-sheet-title"
            data-testid="notification-sheet"
          >
            {/* Drag handle — mobile only */}
            <div
              className="md:hidden mx-auto mt-3 h-1.5 w-12 rounded-full bg-gray-600"
              aria-hidden="true"
            />

            <div className="flex items-center justify-between px-5 py-4">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-purple-400" />
                <h3 id="notification-sheet-title" className="text-white font-bold text-lg">
                  Notifications
                </h3>
                {unread > 0 && (
                  <span className="ml-1 px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-xs font-semibold">
                    {unread} new
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={closeSheet}
                className="p-2 rounded-lg hover:bg-white/5 focus:outline-none"
                aria-label="Close notifications"
                data-testid="notification-sheet-close"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <div
              className="px-3 pb-4 overflow-y-auto"
              style={{ maxHeight: 'calc(75vh - 64px)' }}
              data-testid="notification-list"
            >
              {loading && items.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-500 border-t-transparent mb-3" />
                  <span className="text-sm">Loading…</span>
                </div>
              )}

              {!loading && items.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center px-6">
                  <div className="p-4 rounded-full bg-purple-500/10 mb-4">
                    <Bell className="h-8 w-8 text-purple-400" />
                  </div>
                  <p className="text-gray-300 font-medium mb-1">You&apos;re all caught up</p>
                  <p className="text-gray-500 text-sm">
                    New trade wins, deposits and admin messages will show up here.
                  </p>
                </div>
              )}

              {items.map((n) => {
                const Icon = iconForType(n.type);
                const accent = accentForType(n.type);
                return (
                  <div
                    key={n.id}
                    className="flex items-start gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.03] transition-colors"
                    data-testid={`notification-item-${n.type}`}
                  >
                    <div className={`shrink-0 mt-0.5 p-2 rounded-lg ${accent}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="text-white text-sm font-semibold truncate">{n.title}</p>
                        <span className="text-gray-500 text-xs shrink-0">{timeAgo(n.timestamp)}</span>
                      </div>
                      <p className="text-gray-400 text-sm mt-0.5 leading-snug">{n.message}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default NotificationBell;
